import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFormContext,
} from "react-hook-form";

import { ReorderHandle } from "src/base-components/ReorderHandle";
import { IconButton } from "src/design-system/Icon";
import {
  isValidEnumOptionInput,
  validateUniqueEnumOptions,
} from "src/schema/enumUtils";
import { SchemaEnumInput } from "src/schema/SchemaEnumInput";
import { EnumValueObject, PropertyUI } from "src/schema/schemaMappingUtils";

export type SchemaEnumErrorStatus = Merge<
  FieldError,
  (FieldErrorsImpl<EnumValueObject> | undefined)[]
>;

type Props = {
  onDelete: () => void;
  fieldIndex: number;
  readonly?: boolean;
  id: string;
  deletable: boolean;
  enumFormLocation: string;
  errorStatus?: SchemaEnumErrorStatus;
  withValidation: boolean;
  searchHighlightKey?: string;
};

export const SchemaEnumRow: React.FC<Props> = ({
  readonly = false,
  onDelete,
  fieldIndex,
  id,
  deletable,
  errorStatus,
  enumFormLocation,
  withValidation,
  searchHighlightKey,
}) => {
  const { register, getValues } = useFormContext();
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isDeleteDisabled = readonly || !deletable;

  return (
    <li
      ref={setNodeRef}
      className="ml-3 flex flex-row items-center gap-x-2"
      data-loc={`schema-enum-row-${fieldIndex}`}
      style={style}
    >
      <ReorderHandle
        color="text-gray-500"
        immutable={readonly}
        isGrabbing={isDragging}
        listeners={listeners}
      />
      <SchemaEnumInput
        disabled={readonly}
        errorStatus={
          errorStatus?.at?.(fieldIndex)?.value as unknown as Merge<
            FieldError,
            FieldErrorsImpl<PropertyUI>
          >
        }
        formProps={register(`${enumFormLocation}.${fieldIndex}.value`, {
          required: withValidation,
          ...(withValidation
            ? {
                validate: {
                  unique: (value) =>
                    validateUniqueEnumOptions(
                      value,
                      getValues(enumFormLocation),
                    ),
                  validValue: isValidEnumOptionInput,
                },
              }
            : {}),
        })}
        searchHighlightKey={searchHighlightKey}
      />
      <IconButton
        dataLoc="enum-field-delete"
        disabled={isDeleteDisabled}
        icon={faTrashAlt}
        size="xs"
        onClick={onDelete}
      />
    </li>
  );
};
